import React from 'react';
import { cn } from '@/utils';

export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string;
  alt?: string;
  name?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  verified?: boolean;
  online?: boolean;
}

const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, src, alt, name, size = 'md', verified = false, online = false, ...props }, ref) => {
    const getInitials = (name?: string) => {
      if (!name) return '?';
      return name
        .split(' ')
        .map(part => part.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
    };

    const sizeClasses = {
      xs: 'w-6 h-6 text-xs',
      sm: 'w-8 h-8 text-sm',
      md: 'w-10 h-10 text-base',
      lg: 'w-12 h-12 text-lg',
      xl: 'w-16 h-16 text-xl',
      '2xl': 'w-20 h-20 text-2xl',
    };

    const verifiedIconSize = {
      xs: 'w-3 h-3',
      sm: 'w-3 h-3',
      md: 'w-4 h-4',
      lg: 'w-4 h-4',
      xl: 'w-5 h-5',
      '2xl': 'w-6 h-6',
    };

    const onlineIndicatorSize = {
      xs: 'w-2 h-2',
      sm: 'w-2 h-2',
      md: 'w-3 h-3',
      lg: 'w-3 h-3',
      xl: 'w-4 h-4',
      '2xl': 'w-4 h-4',
    };

    return (
      <div
        className={cn('relative inline-flex', className)}
        ref={ref}
        {...props}
      >
        <div
          className={cn(
            'relative flex items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-accent-500 text-white font-semibold overflow-hidden',
            sizeClasses[size]
          )}
        >
          {src ? (
            <img
              src={src}
              alt={alt || name}
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback to initials if image fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          ) : (
            <span className="select-none">
              {getInitials(name)}
            </span>
          )}
        </div>

        {/* Verified badge */}
        {verified && (
          <div className="absolute -top-1 -right-1 bg-primary-500 rounded-full p-1">
            <svg
              className={cn('text-white', verifiedIconSize[size])}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        )}

        {/* Online indicator */}
        {online && (
          <div
            className={cn(
              'absolute bottom-0 right-0 rounded-full bg-success-500 border-2 border-tg-bg',
              onlineIndicatorSize[size]
            )}
          />
        )}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';

export { Avatar };
